import React from 'react';
import { motion } from 'framer-motion';
import { 
  IconBriefcase, 
  IconCode, 
  IconUsers, 
  IconChalkboard 
} from './icons';
import { experiences } from '../data';

const About: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const technicalSkills = [
    {
      icon: IconCode,
      title: 'Lenguajes',
      items: ['Objective-C', 'PHP', 'JavaScript', 'HTML5', 'CSS3'],
      color: 'text-blue-600'
    },
    {
      icon: IconBriefcase,
      title: 'Frameworks',
      items: ['JQuery', 'Bootstrap', 'Cocoa-Touch (iOS)', 'React', 'Vue.js'],
      color: 'text-green-600'
    },
    {
      icon: IconUsers,
      title: 'Metodologías',
      items: ['Scrum Manager Certificado', 'JIRA', 'Metodologías Ágiles', 'Gestión de Equipos'],
      color: 'text-purple-600'
    },
    {
      icon: IconChalkboard,
      title: 'Actividades',
      items: ['Betabeers Badajoz', 'Seminario UNEX', 'Talleres iOS', 'Charlas técnicas'],
      color: 'text-red-600'
    }
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Sobre <span className="gradient-text">Mí</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Mi trayectoria como desarrollador de software y emprendedor
          </p>
        </motion.div>

        {/* About Content */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Personal Story */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="card">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Mi Historia</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Desde bien joven tenía muy claro que quería dedicarme al maravilloso mundo de la informática. 
                  Por desgracia vivía en un pueblito de Badajoz, donde por supuesto no había nadie con quien compartir 
                  mis aficiones y pasiones por este mundo.
                </p>
                <p>
                  Cuando llegué a la Universidad, en la escuela politécnica de Mérida me encontré con ese caldo de 
                  cultivo que necesitaba para expandirme en este mundo. Fue en esos momentos de "necesidad" cuando 
                  me di cuenta que podía aprender todo lo que quisiera y necesitara por mis propios medios.
                </p>
                <p>
                  De esta forma aprendí PHP y administración de bases de datos en MySQL. Unos años después, creamos 
                  nuestra primera empresa, <span className="font-semibold text-primary-600">Synapse</span> Asesores 
                  Informáticos, S.L. junto con un muy buen amigo. Más tarde llegaría{' '}
                  <span className="font-semibold text-primary-600">Creapptiva</span>, dedicada al diseño de apps móviles.
                </p>
                <p>
                  Desde entonces ha llovido mucho, pero seguimos con la misma ilusión, o más si cabe.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Experience Timeline */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="card">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Experiencia Profesional</h3>
              <div className="space-y-6">
                {experiences.map((exp, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="relative pl-8 border-l-2 border-primary-200"
                  >
                    <div className="absolute -left-2 top-0 w-4 h-4 bg-primary-600 rounded-full"></div>
                    <div className="mb-2">
                      <h4 className="font-bold text-gray-900">{exp.position}</h4>
                      <p className="text-primary-600 font-medium">{exp.company}</p>
                      <p className="text-sm text-gray-500">{exp.period}</p>
                    </div>
                    <p className="text-gray-700 text-sm">{exp.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Technical Skills Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <motion.h3
            variants={itemVariants}
            className="text-2xl font-bold text-gray-900 text-center mb-8"
          >
            Aptitudes Técnicas
          </motion.h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technicalSkills.map((skillGroup, index) => (
              <motion.div
                key={skillGroup.title}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="card"
              >
                <skillGroup.icon className={`text-3xl ${skillGroup.color} mb-4`} />
                <h4 className="font-bold text-gray-900 mb-3">{skillGroup.title}</h4>
                <ul className="space-y-2">
                  {skillGroup.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-sm text-gray-700 flex items-center">
                      <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Projects Experience */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="card"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Proyectos Destacados</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">15+</div>
              <p className="text-gray-700">Años de experiencia</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">50+</div>
              <p className="text-gray-700">Proyectos completados</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">2</div>
              <p className="text-gray-700">Empresas fundadas</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;